// Dashboard.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  FiCopy,
  FiEdit,
  FiTrash2,
  FiClock,
  FiTruck,
  FiCheck,
  FiArchive,
} from "react-icons/fi";
import SharedLayout from "./SharedLayout";
import "./App.css";
import apiClient from "./services/apiClient";
import { useParams, useNavigate } from "react-router-dom";
import InfoModal from "./modals/InfoModals";
import ConfirmModal from "./modals/ConfirmModals";

const Dashboard = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  // Info modal
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const openInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  // Confirm modal
  const [confirm, setConfirm] = useState({
    open: false,
    title: "",
    description: "",
    confirmText: "",
    confirmBtnClass: "",
    cancelBtnClass: "",
    onConfirm: null,
  });
  const openConfirm = (payload) =>
    setConfirm({
      open: true,
      title: payload.title,
      description: payload.description,
      confirmText: payload.confirmText,
      confirmBtnClass: payload.confirmBtnClass,
      cancelBtnClass: payload.cancelBtnClass,
      onConfirm: payload.onConfirm,
    });
  const closeConfirm = () =>
    setConfirm((p) => ({ ...p, open: false, onConfirm: null }));

  // Progress donut
  const renderProgressCircle = (progress, size = 60, strokeWidth = 6) => {
    const pct = Math.max(0, Math.min(100, Number(progress || 0)));
    const radius = size / 2 - strokeWidth / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (pct / 100) * circumference;

    return (
      <div className="flex flex-col items-center">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#e6e6e6"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#07183c"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: "stroke-dashoffset 0.5s ease" }}
          />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dy=".3em"
            fontSize={size / 5}
            fill="#333"
            fontWeight="bold"
          >
            {pct}%
          </text>
        </svg>
      </div>
    );
  };

  // Fetch courses (ARCHIVED hariç)
  const fetchCourses = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // v2 API: admin course list endpoint is /courses/admin
      const data = await apiClient.get("/courses/admin");
      const list = Array.isArray(data) ? data : data?.courses || [];

      const formatted = list.map((c) => ({
        id: c.id,
        status: c.status, // 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
        title: c.name,
        totalEnrollments: c.totalEnrollments,
        averageProgress: c.averageProgress,
      }));

      const visibleCourses = formatted.filter((x) => x.status !== "ARCHIVED");
      setCourses(visibleCourses);

      setStats([
        { title: "TOPLAM İÇERİK", count: visibleCourses.length },
        { title: "TOPLAM ÖĞRENCİ", count: data?.learnersCount || 0 },
        { title: "TOPLAM GRUPLAR", count: data?.groupsCount || 0 },
      ]);
    } catch (error) {
      console.error("Error fetching courses:", error);
      openInfo("error", "Yükleme Hatası", "Kurslar yüklenemedi.");
      setCourses([]);
      setStats([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Fetch courses on mount and after actions (duplicate, delete, archive)
  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // Duplicate course
  const handleDuplicateCourse = (courseId, courseTitle) => {
    openConfirm({
      title: "Kopyala",
      description: `"${courseTitle}" kursunu kopyalamak istiyor musun?`,
      confirmText: "Evet, kopyala",
      confirmBtnClass: "bg-blue-600 hover:bg-blue-700 text-white",
      cancelBtnClass:
        "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
      onConfirm: async () => {
        try {
          const data = await apiClient.post(`/courses/${courseId}/duplicate`);

          console.log(data);
          closeConfirm();
          openInfo("success", "Kopyalama Başarılı", "Kurs kopyalandı.");
          fetchCourses();
        } catch (error) {
          console.error("Error duplicating course:", error);
          closeConfirm();
          openInfo(
            "error",
            "Kopyalama Hatası",
            "Kurs kopyalanırken sorun oluştu.",
          );
        }
      },
    });
  };

  // Archive course
  const handleArchiveCourse = (courseId, courseTitle) => {
    openConfirm({
      title: "Arşivle",
      description: `"${courseTitle}" kursunu arşivlemek istiyor musun?`,
      confirmText: "Evet, arşivle",
      confirmBtnClass: "bg-amber-600 hover:bg-amber-700 text-white",
      cancelBtnClass:
        "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
      onConfirm: async () => {
        try {
          await apiClient.put(`/courses/${courseId}/archive`, {});
          // Dashboard arşiv dışı gösterdiği için listeden çıkar
          setCourses((prev) => prev.filter((c) => c.id !== courseId));
          setStats((prev) =>
            prev.map((s) =>
              s.title === "TOPLAM İÇERİK" ? { ...s, count: s.count - 1 } : s,
            ),
          );
          closeConfirm();
          openInfo("success", "İşlem başarılı", "Kurs arşivlendi.");
        } catch (error) {
          console.error("Archive error:", error);
          closeConfirm();
          openInfo("error", "Hata", "Kurs arşivlenirken sorun oluştu.");
        }
      },
    });
  };

  // Delete course
  const handleDeleteCourse = (courseId, courseTitle) => {
    openConfirm({
      title: "Silme Onayı",
      description: `"${courseTitle}" kursunu silmek istiyor musun? Bu işlem geri alınamaz.`,
      confirmText: "Evet, sil",
      confirmBtnClass: "bg-red-600 hover:bg-red-700 text-white",
      cancelBtnClass:
        "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
      onConfirm: async () => {
        try {
          await apiClient.delete(`/courses/${courseId}`);
          setCourses((prev) => prev.filter((c) => c.id !== courseId));
          setStats((prev) =>
            prev.map((s) =>
              s.title === "TOPLAM İÇERİK" ? { ...s, count: s.count - 1 } : s,
            ),
          );
          closeConfirm();
          openInfo("success", "Silindi", "Kurs başarıyla silindi.");
        } catch (error) {
          console.error("Delete error:", error);
          closeConfirm();
          openInfo("error", "Hata", "Kurs silinirken sorun oluştu.");
        }
      },
    });
  };

  return (
    <SharedLayout pageTitle="ANASAYFA">
      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white p-4 sm:p-5 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-sm sm:text-base font-medium text-gray-700">
                {stat.title}
              </h3>
              <div className="h-16 w-16 bg-[#07183c] rounded-full flex items-center justify-center">
                <span className="text-white text-base font-bold">
                  {stat.count}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Courses (archived excluded) */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">İÇERİKLER</h2>

        {loading ? (
          <div className="text-sm text-gray-500">Yükleniyor…</div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8">
            {courses.map((course) => (
              <div
                key={course.id}
                className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Actions */}
                {course.status === "DRAFT" ? (
                  <div className="flex flex-wrap justify-end gap-2 mb-3">
                    <button
                      className="text-gray-500 hover:text-blue-600 p-1"
                      title="Kopyala"
                      onClick={() =>
                        handleDuplicateCourse(course.id, course.title)
                      }
                    >
                      <FiCopy size={14} />
                    </button>
                    <button
                      className="text-gray-500 hover:text-blue-600 p-1"
                      title="Düzenle"
                      onClick={() =>
                        navigate(`/${userId}/edit-course/${course.id}`)
                      }
                    >
                      <FiEdit size={14} />
                    </button>
                    <button
                      className="text-gray-500 hover:text-red-600 p-1"
                      title="Sil"
                      onClick={() =>
                        handleDeleteCourse(course.id, course.title)
                      }
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap justify-end gap-2 mb-3">
                    <button
                      className="text-gray-500 hover:text-blue-600 p-1"
                      title="Kopyala"
                      onClick={() =>
                        handleDuplicateCourse(course.id, course.title)
                      }
                    >
                      <FiCopy size={14} />
                    </button>
                    <button
                      className="text-gray-500 hover:text-amber-600 p-1"
                      title="Arşivle"
                      onClick={() =>
                        handleArchiveCourse(course.id, course.title)
                      }
                    >
                      <FiArchive size={14} />
                    </button>
                    <button
                      className="text-gray-500 hover:text-red-600 p-1"
                      title="Sil"
                      onClick={() =>
                        handleDeleteCourse(course.id, course.title)
                      }
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                )}

                {/* Status pill */}
                <div
                  className={`inline-flex items-center px-2 py-1 rounded-md mb-2 ${
                    course.status === "DRAFT"
                      ? "bg-purple-100 text-purple-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {course.status === "DRAFT" ? (
                    <>
                      <FiEdit className="mr-1" size={12} />
                      <span className="text-xs font-medium">TASLAK</span>
                    </>
                  ) : (
                    <>
                      <FiClock className="mr-1" size={12} />
                      <span className="text-xs font-medium">PLANLANDI</span>
                    </>
                  )}
                </div>

                <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-1 break-words">
                  {course.title}
                </h3>

                <div className="flex flex-col sm:flex-row justify-between gap-6 mt-4">
                  <div className="flex flex-col items-center w-full sm:w-1/2">
                    <div className="h-[90px] w-[90px] bg-[#07183c] rounded-full flex items-center justify-center">
                      <span className="text-white text-xl font-bold">
                        {course.totalEnrollments}
                      </span>
                    </div>
                    <div className="flex items-center mt-1">
                      <FiTruck className="mr-1 text-gray-500" size={12} />
                      <span className="text-xs text-gray-500">
                        TOPLAM KAYITLAR
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col items-center w-full sm:w-1/2">
                    <div className="scale-90 sm:scale-100 origin-center">
                      {renderProgressCircle(
                        Number(course.averageProgress || 0),
                        90,
                        5,
                      )}
                    </div>
                    <div className="flex items-center mt-1">
                      <FiCheck className="mr-1 text-gray-500" size={12} />
                      <span className="text-xs text-gray-500">
                        ORTALAMA TAMAMLANAN
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {courses.length === 0 && (
              <div className="text-sm text-gray-500">
                Gösterilecek kurs bulunamadı.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <InfoModal
        open={info.open}
        type={info.type}
        title={info.title}
        message={info.message}
        onClose={closeInfo}
      />
      <ConfirmModal
        open={confirm.open}
        title={confirm.title}
        description={confirm.description}
        confirmText={confirm.confirmText}
        confirmBtnClass={confirm.confirmBtnClass}
        cancelBtnClass={confirm.cancelBtnClass}
        onCancel={closeConfirm}
        onConfirm={confirm.onConfirm}
      />
    </SharedLayout>
  );
};

export default Dashboard;
